import React, { useState, useEffect, useContext } from "react";
import { AuthContext } from "../../../contexts/AuthContext";
import { Button, Row, Col, Table, Input, Label } from "reactstrap";
import axios from "axios";
import Swal from "sweetalert2";
import Header from "../../../layout/Header/Header";
import { CheckFecha } from "./CheckFecha";
import { CampoAuto } from "./CampoAuto";
import FichasTecnicasModal from "./FichasTecnicasModal";
import { useAutoGuardado, fondoFila, SELLO_ES, COLOR_SELLO } from "./autoGuardado";

// Seguimiento de produccion del lado de Mexico, por MODELO dentro de cada OC
// (nov-2026; antes era por orden completa). Cada dato se guarda en todos los
// renglones de ese modelo en la orden. Si el modelo todavia no tiene datos
// propios se muestran los que se habian capturado a nivel orden.
// Va en espanol porque lo captura el equipo de aca; el de proveedor (China) va en ingles.
// Los campos marcados como "check" se palomean y el sistema estampa solo la fecha.
// Lo que nadie ha capturado sale en azul con lo que el sistema ya sabe: PP,
// pais de origen, compradora, FOB, cancelacion, CRD (la fecha de carga que puso
// el proveedor en Tracking China), ficha tecnica (fecha del primer archivo) y
// comentarios (comentario interno del layout). Lo tecleado manda.
const CAMPOS = [
  { key: "pp", label: "PP", w: 90 },
  { key: "pi", label: "PI", w: 110 },
  { key: "fechaPi", label: "Fecha PI", w: 110 },
  { key: "paisOrigen", label: "Pais origen", w: 110 },
  { key: "tipoPP", label: "Tipo", w: 110 },
  { key: "compradora", label: "Compradora", w: 120 },
  { key: "pedidoEnviadoProv", label: "Pedido enviado", w: 95, tipo: "check" },
  { key: "coloridoEstampado", label: "Colorido / estampado", w: 95, tipo: "check" },
  { key: "etiqPrecio", label: "Etiq. precio", w: 95, tipo: "check" },
  { key: "etiqMarca", label: "Etiq. marca", w: 95, tipo: "check" },
  { key: "monarch", label: "Monarch", w: 95, tipo: "check" },
  { key: "fichaTecnica", label: "Ficha tecnica", w: 130, tipo: "check" },
  { key: "gs1", label: "GS1", w: 95, tipo: "check" },
  { key: "altaPortal", label: "Alta en portal", w: 95, tipo: "check" },
  { key: "archOC", label: "Archivo OC", w: 95, tipo: "check" },
  { key: "dropBox", label: "Dropbox", w: 95, tipo: "check" },
  { key: "fechaFob", label: "Fecha FOB", w: 110 },
  { key: "crd", label: "CRD", w: 110 },
  { key: "fechaCancelacion", label: "Cancelacion", w: 110 },
  { key: "auditoria", label: "Auditoria", w: 95, tipo: "check" },
  { key: "fechaConfirmacion", label: "Fecha confirmacion", w: 110 },
  { key: "cita", label: "Cita", w: 130 },
  { key: "ampliacion", label: "Ampliacion", w: 130 },
  { key: "comentariosSeg", label: "Comentarios", w: 180 },
  { key: "comentSemanal", label: "Comentario semanal", w: 200 },
];

function SeguimientoMX() {
  const { user } = useContext(AuthContext);
  const URL_ORDENES_COMPRA = process.env.REACT_APP_URL_ORDENES_COMPRA;
  const URL_ARTICULOS_OC = process.env.REACT_APP_URL_ARTICULOS_OC;
  const URL_SEGUIMIENTOS = process.env.REACT_APP_URL_SEGUIMIENTOS;
  const headers = { Authorization: `Bearer: ${localStorage.getItem("app_token")}` };

  const [filas, setFilas] = useState([]);
  const [busqueda, setBusqueda] = useState("");
  const [proveedor, setProveedor] = useState("");
  const [ordenCompra, setOrdenCompra] = useState("");
  // modelo cuyas fichas tecnicas se estan viendo
  const [filaFichas, setFilaFichas] = useState(null);

  // Cada renglon (modelo) se guarda solo; no hay boton de guardar. El dato va a
  // todos los renglones de la OC que son de ese modelo.
  const { programa, estado } = useAutoGuardado((fila) => {
    const body = {};
    CAMPOS.forEach((c) => { body[c.key] = fila[c.key] === "" ? "NA" : String(fila[c.key]); });
    return Promise.all(fila.lineas.map((id) => axios.patch(`${URL_ARTICULOS_OC}/${id}`, body, { headers })));
  });

  useEffect(() => {
    Promise.all([
      axios.get(URL_ORDENES_COMPRA, { headers }),
      axios.get(URL_ARTICULOS_OC, { headers }),
      // si fallan los automaticos la pantalla sigue funcionando sin ellos
      axios.get(`${URL_SEGUIMIENTOS}Automaticos`, { headers }).catch(() => ({ data: {} })),
    ])
      .then(([resOC, resLin, resAuto]) => {
        const autos = resAuto.data || {};
        const ocs = {};
        (resOC.data || []).filter((a) => a.is_active === "Si").forEach((o) => { ocs[o._id] = o; });
        const vacio = (v) => v === undefined || v === null || v === "" || v === "NA";
        const porModelo = new Map();
        (resLin.data || []).forEach((l) => {
          const refOC = l.ordenesCompra?.[0];
          const oc = ocs[refOC?._id || refOC];
          const art = l.articulos?.[0];
          if (!oc || !art || l.is_active === "No") return;
          const k = `${oc._id}|${art._id}|${l.renglon || 1}`;
          if (!porModelo.has(k)) {
            const base = {
              _id: k, lineas: [], numero: oc.idOrdenCompra,
              proveedor: oc.proveedores?.[0]?.nombre_comercial || "",
              modelo: `${art.codigo || ""} ${art.nombre || ""}`.trim(), colores: new Set(), piezas: 0, status: oc.status,
              idArticulo: art._id, fichas: art.fichasTecnicas || [], auto: {},
            };
            // lo que se habia capturado a nivel orden sirve de punto de partida
            CAMPOS.forEach((c) => { base[c.key] = vacio(oc[c.key]) ? "" : oc[c.key]; });
            base.propios = false;
            porModelo.set(k, base);
          }
          const f = porModelo.get(k);
          f.lineas.push(l._id);
          // automatico del modelo: el primero que tenga dato entre sus renglones
          const au = autos[l._id] || {};
          CAMPOS.forEach((c) => { if (!f.auto[c.key] && au[c.key]) f.auto[c.key] = au[c.key]; });
          f.piezas += Number(l.cantidad) || 0;
          if (l.colores?.[0]?.name) f.colores.add(l.colores[0].name);
          // lo guardado en el renglon del modelo manda sobre lo de la orden
          CAMPOS.forEach((c) => { if (!vacio(l[c.key])) { f[c.key] = l[c.key]; f.propios = true; } });
        });
        const data = [...porModelo.values()]
          .map((f) => ({ ...f, colores: [...f.colores].join(" · ") }))
          .sort((a, b) => (a.numero < b.numero ? 1 : a.numero > b.numero ? -1 : a.modelo > b.modelo ? 1 : -1));
        setFilas(data);
      })
      .catch(() => Swal.fire({ icon: "error", title: "Ups...", text: "No se pudieron cargar las PO" }));
  }, []); // eslint-disable-line

  // Las casillas se mandan al momento; lo que se escribe espera a que la fila se quede quieta.
  const cambia = (id, key, valor, inmediato = false) => {
    const actual = filas.find((f) => f._id === id);
    if (!actual) return;
    const fila = { ...actual, [key]: valor };
    setFilas((prev) => prev.map((f) => (f._id === id ? fila : f)));
    programa(fila, inmediato);
  };

  // catalogos de los filtros, sacados de lo que realmente hay cargado
  const proveedores = Array.from(new Set(filas.map((f) => f.proveedor).filter(Boolean))).sort();
  // las OC del selector se acotan al proveedor elegido, para no ofrecer combinaciones vacias
  const ordenes = filas
    .filter((f) => (proveedor ? f.proveedor === proveedor : true))
    .map((f) => f.numero)
    .sort((a, b) => b - a);

  // las fichas son del modelo: se actualizan en todas sus filas
  const cambiaFichas = (idArticulo, fichas) => {
    setFilas((prev) => prev.map((f) => (f.idArticulo === idArticulo
      ? { ...f, fichas, auto: { ...f.auto, fichaTecnica: fichas[0]?.fecha || "" } } : f)));
    setFilaFichas((x) => (x && x.idArticulo === idArticulo ? { ...x, fichas } : x));
  };

  const limpiar = () => { setProveedor(""); setOrdenCompra(""); setBusqueda(""); };

  const filtradas = filas.filter((f) => {
    if (proveedor && f.proveedor !== proveedor) return false;
    if (ordenCompra && String(f.numero) !== String(ordenCompra)) return false;
    const t = `${f.numero} ${f.proveedor} ${f.modelo} ${f.pp} ${f.tipoPP} ${f.compradora}`.toLowerCase();
    return t.includes(busqueda.toLowerCase());
  });

  const totalPiezas = filtradas.reduce((s, f) => s + Number(f.piezas || 0), 0);

  return (
    <>
      <Header />
      <br />
      <br />
      {user.menu_procesos ? (
        <div className="card container col-12">
          <Row>
            <Col md={3}>
              <Button href="/MenuOperacion" className="btn btn-danger" size="sm">Regresar</Button>
            </Col>
            <Col md={3}>
              <Label>Proveedor</Label>
              <Input type="select" bsSize="sm" value={proveedor}
                onChange={(e) => { setProveedor(e.target.value); setOrdenCompra(""); }}>
                <option value="">Todos</option>
                {proveedores.map((p) => (<option key={p} value={p}>{p}</option>))}
              </Input>
            </Col>
            <Col md={2}>
              <Label>PO</Label>
              <Input type="select" bsSize="sm" value={ordenCompra}
                onChange={(e) => setOrdenCompra(e.target.value)}>
                <option value="">Todas</option>
                {ordenes.map((n) => (<option key={n} value={n}>{n}</option>))}
              </Input>
            </Col>
            <Col md={3}>
              <Label>Buscar</Label>
              <Input type="text" bsSize="sm" placeholder="OC, modelo, proveedor, PP, compradora"
                value={busqueda} onChange={(e) => setBusqueda(e.target.value)} />
            </Col>
            <Col md={1} style={{ display: "flex", alignItems: "flex-end" }}>
              <Button color="secondary" size="sm" onClick={limpiar}>Limpiar</Button>
            </Col>
          </Row>
          <h3 align="center">Seguimiento de produccion — Mexico</h3>
          <p align="center" style={{ color: "#64748b", fontSize: "0.8rem", marginBottom: 8 }}>
            {filtradas.length} modelo(s) de {new Set(filtradas.map((f) => f.numero)).size} orden(es) · {new Intl.NumberFormat("es-MX").format(totalPiezas)} piezas.
            Los cambios se guardan solos. Las casillas ademas anotan la fecha en que se palomearon.
            Lo azul sale solo del pedido, la OC o el embarque; escribe encima para cambiarlo.
          </p>
          <div style={{ width: "100%", overflowX: "auto" }}>
            <Table size="sm" striped bordered style={{ whiteSpace: "nowrap", minWidth: "2100px" }}>
              <thead>
                <tr>
                  <th style={{ position: "sticky", left: 0, backgroundColor: "#fff", zIndex: 2 }}>PO</th>
                  <th>Proveedor</th>
                  <th>Modelo</th>
                  <th>Colores</th>
                  <th align="right">Piezas</th>
                  {CAMPOS.map((c) => (<th key={c.key} style={{ minWidth: c.w }}>{c.label}</th>))}
                </tr>
              </thead>
              <tbody>
                {filtradas.map((f) => (
                  <tr key={f._id} style={{ background: fondoFila(estado[f._id]) }}>
                    <td style={{ position: "sticky", left: 0, backgroundColor: fondoFila(estado[f._id]) || "#fff", zIndex: 1, fontWeight: 600 }}>
                      {f.numero}
                      {estado[f._id] ? (
                        <small style={{ display: "block", fontSize: "0.62rem", fontWeight: 400, color: COLOR_SELLO[estado[f._id]] }}>
                          {SELLO_ES[estado[f._id]]}
                        </small>
                      ) : null}
                    </td>
                    <td>{f.proveedor}</td>
                    <td style={{ fontWeight: 600 }}>{f.modelo}</td>
                    <td style={{ fontSize: "0.75rem", color: "#64748b" }}>{f.colores}</td>
                    <td align="right">{new Intl.NumberFormat("es-MX").format(f.piezas)}</td>
                    {CAMPOS.map((c) => (
                      <td key={c.key} style={{ minWidth: c.w, textAlign: c.tipo === "check" ? "center" : undefined }}>
                        {c.key === "fichaTecnica" ? (
                          <div style={{ display: "flex", gap: 6, alignItems: "center", justifyContent: "center" }}>
                            <CheckFecha valor={f[c.key]} auto={f.auto[c.key]} onChange={(v) => cambia(f._id, c.key, v, true)} />
                            <Button size="sm" color={f.fichas.length ? "info" : "secondary"} outline={!f.fichas.length}
                              style={{ padding: "1px 6px" }} title="Ver y subir las fichas técnicas del modelo"
                              onClick={() => setFilaFichas(f)}>
                              <i className="fas fa-paperclip" /> {f.fichas.length || ""}
                            </Button>
                          </div>
                        ) : c.tipo === "check" ? (
                          <CheckFecha valor={f[c.key]} auto={f.auto[c.key]} onChange={(v) => cambia(f._id, c.key, v, true)} />
                        ) : (
                          <CampoAuto tipo={c.key.startsWith("fecha") || c.key === "crd" ? "date" : "text"}
                            valor={f[c.key]} auto={f.auto[c.key]}
                            onChange={(v) => cambia(f._id, c.key, v)} />
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
                {filtradas.length === 0 ? (
                  <tr><td colSpan={5 + CAMPOS.length} align="center" style={{ color: "#94a3b8" }}>
                    No hay ordenes con esos filtros.
                  </td></tr>
                ) : null}
              </tbody>
            </Table>
          </div>
          <br />
          <FichasTecnicasModal
            abierto={!!filaFichas}
            cerrar={() => setFilaFichas(null)}
            modelo={filaFichas?.modelo}
            idArticulo={filaFichas?.idArticulo}
            fichas={filaFichas?.fichas || []}
            onCambio={(fichas) => cambiaFichas(filaFichas.idArticulo, fichas)}
          />
        </div>
      ) : undefined}
    </>
  );
}

export default SeguimientoMX;
